// src/components/QuickCaptureModal.js
import React, { useRef, useState } from 'react'
import CaptureIcon from './CaptureIcon'
import { supabase } from '../supabaseClient'
import { saveMoment } from '../momentsService'
import { CITIES, DAYS } from '../data/content'

const PLACEHOLDER = {
  nyc: 'I am grateful for…',
  paris: 'Je suis reconnaissant pour…',
  london: 'I am grateful for…',
  milan: 'Sono grato per…',
  berlin: 'Ich bin dankbar für…',
  madrid: 'Estoy agradecido por…',
}

const CITY_META = {
  nyc:    { label: 'New York', color: '#EE352E' },
  paris:  { label: 'Paris',    color: '#5B2D8E' },
  london: { label: 'London',   color: '#E32017' },
  milan:  { label: 'Milan',    color: '#CC0000' },
  berlin: { label: 'Berlin',   color: '#F0CA00' },
  madrid: { label: 'Madrid',   color: '#E4032E' },
}

const PinSVG = ({ color, size = 11 }) => (
  <svg width={size} height={size * 1.35} viewBox="0 0 14 18" fill="none" style={{ flexShrink: 0, display: 'block' }}>
    <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5C5.62 9.5 4.5 8.38 4.5 7S5.62 4.5 7 4.5 9.5 5.62 9.5 7 8.38 9.5 7 9.5z" fill={color}/>
  </svg>
)

export default function QuickCaptureModal({ session, currentCity, allEntries, onClose, onSaved, defaultDay = null, hideStationPicker = false }) {
  const fileRef = useRef(null)
  const [photo, setPhoto] = useState(null)
  const [grat, setGrat] = useState('')
  const [selectedCity] = useState(currentCity || 'nyc')
  const [selectedDay] = useState(defaultDay)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [location, setLocation] = useState(null)
  const [locLoading, setLocLoading] = useState(false)
  const [locError, setLocError] = useState(null)

  const cityColor = CITY_META[selectedCity]?.color || '#EE352E'
  const cityLabel = CITY_META[selectedCity]?.label || ''

  const handleGetLocation = () => {
    if (!navigator.geolocation) { setLocError('Not supported'); return }
    setLocLoading(true); setLocError(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`, { headers: { 'Accept-Language': 'en' } })
          const data = await res.json()
          const addr = data.address || {}
          const neighbourhood = addr.neighbourhood || addr.suburb || addr.quarter || addr.road || ''
          const city = addr.city || addr.town || addr.village || ''
          setLocation({ label: neighbourhood && city ? `${neighbourhood}, ${city}` : neighbourhood || city || 'Here' })
        } catch(e) { setLocation({ label: `${lat.toFixed(3)}, ${lng.toFixed(3)}` }) }
        setLocLoading(false)
      },
      () => { setLocError('Location denied'); setLocLoading(false) },
      { timeout: 8000, maximumAge: 60000 }
    )
  }

  const handlePhoto = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => setPhoto(ev.target.result)
    reader.readAsDataURL(file)
  }

  const handleSave = async () => {
    if (!photo || !grat.trim()) return
    const dayToSave = selectedDay || defaultDay || 1
    setSaving(true)

    // Save to new moments table
    const { data: moment, error: momentErr } = await saveMoment({
      userId: session.user.id,
      imageUrl: photo,
      text: grat.trim(),
      locationName: location?.label || null,
      city: selectedCity,
      dayNumber: dayToSave,
    })

    if (momentErr) {
      console.error('Moment save failed:', momentErr.message)
      // Fallback: also try saving image to journal_entries for display
    }

    // Also save image to journal_entries so it shows in existing moment cards
    await supabase
      .from('journal_entries')
      .update({ images: [photo] })
      .eq('user_id', session.user.id)
      .eq('city', selectedCity)
      .eq('day_number', dayToSave)

    setSaved(true)
    onSaved && onSaved({
      city: selectedCity,
      day: dayToSave,
      img: photo,
      grat: grat.trim(),
      location: location?.label || null,
      momentId: moment?.id || null,
    })
    setTimeout(() => { setSaved(false); onClose() }, 900)
    setSaving(false)
  }

  const canSave = photo && grat.trim() && (selectedDay || hideStationPicker)

  return (
    <div className="qc-backdrop" onClick={onClose}>
      <div className="qc-modal" onClick={e => e.stopPropagation()}>

        {/* ── PHOTO AREA — full bleed top ── */}
        <div
          className="qc-photo-area"
          onClick={() => !photo && fileRef.current?.click()}
        >
          {photo ? (
            <>
              <img src={photo} alt="moment" className="qc-photo-img" />
              <div className="qc-photo-gradient" />

              {/* City pill */}
              <div className="qc-city-pill-overlay">
                <div className="qc-city-dot" style={{ background: cityColor }} />
                <span className="qc-city-pill-label">{cityLabel.toUpperCase()}</span>
              </div>

              {/* Retake */}
              <button className="qc-retake-btn" onClick={e => { e.stopPropagation(); setPhoto(null); setGrat('') }}>
                ↺ RETAKE
              </button>

              {/* Gratitude overlaid on photo */}
              <div className="qc-grat-overlay">
                {grat
                  ? <p className="qc-grat-on-photo">"{grat}"</p>
                  : <p className="qc-grat-placeholder-photo">Your grateful moment appears here…</p>
                }
                {location && (
                  <div className="qc-location-on-photo">
                    <PinSVG color={cityColor} size={9} />
                    <span>{location.label}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Empty — tap to capture */
            <div className="qc-empty-state">
              <div className="qc-icon-pulse" style={{ '--city': cityColor }}>
                <CaptureIcon color={cityColor} size={72} />
              </div>
              <span className="qc-tap-label">CAPTURE THE MOMENT</span>
            </div>
          )}

          <input ref={fileRef} type="file" accept="image/*" capture="environment"
            style={{ display: 'none' }} onChange={handlePhoto} />
        </div>

        {/* ── BOTTOM PANEL ── */}
        <div className="qc-bottom">

          {/* Close */}
          <button className="qc-close-btn" onClick={onClose}>✕</button>

          {/* Gratitude input */}
          <textarea
            className="qc-textarea"
            placeholder={PLACEHOLDER[selectedCity]}
            value={grat}
            onChange={e => setGrat(e.target.value)}
            rows={2}
            style={{ borderColor: grat ? cityColor + '55' : undefined }}
          />

          {/* Location — inline, no box */}
          <div className="qc-location-row">
            {location ? (
              <>
                <PinSVG color={cityColor} size={11} />
                <span className="qc-location-text">{location.label}</span>
                <button className="qc-location-clear" onClick={() => setLocation(null)}>✕</button>
              </>
            ) : (
              <button className="qc-location-add" style={{ color: cityColor }} onClick={handleGetLocation} disabled={locLoading}>
                <PinSVG color={cityColor} size={11} />
                {locLoading ? 'Getting location…' : 'Add location'}
              </button>
            )}
            {locError && <span className="qc-loc-error">{locError}</span>}
          </div>

          {/* Save */}
          <button
            className="qc-save"
            style={{
              background: saved ? '#4CAF50' : canSave ? cityColor : undefined,
              boxShadow: canSave && !saved ? `0 6px 24px ${cityColor}44` : 'none',
              color: canSave || saved ? '#fff' : undefined,
            }}
            disabled={saving || saved || !canSave}
            onClick={handleSave}
          >
            {saved ? '✓ Saved' : saving ? 'Saving…' : '✦  Save Moment'}
          </button>

        </div>
      </div>
    </div>
  )
}
